import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { COMMO_SHARED_ENVIRONMENT_CONFIG } from '@sankarah/commo-shared-environment';
import { firstValueFrom } from 'rxjs';
import { PushSubscriptionService } from './push-subscription.service';

function subscriptionKeys(sub: PushSubscription): {
  endpoint: string;
  p256dh: string;
  auth: string;
} {
  const json = sub.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.['p256dh'];
  const auth = json.keys?.['auth'];
  if (!endpoint || !p256dh || !auth) {
    throw new Error('Incomplete push subscription');
  }
  return { endpoint, p256dh, auth };
}

@Injectable({ providedIn: 'root' })
export class PushNotificationsFacade {
  private readonly swPush = inject(SwPush);
  private readonly pushSubscriptionService = inject(PushSubscriptionService);
  private readonly env = inject(COMMO_SHARED_ENVIRONMENT_CONFIG);
  private readonly router = inject(Router);

  private clickListenerStarted = false;

  /** SW 有効かつ VAPID 公開鍵があるときだけ購読可能 */
  get isSupported(): boolean {
    return (
      this.swPush.isEnabled &&
      typeof Notification !== 'undefined' &&
      !!this.env.vapidPublicKey?.trim()
    );
  }

  /** 現在この端末で購読中か（ブラウザ側） */
  async hasBrowserSubscription(): Promise<boolean> {
    if (!this.swPush.isEnabled) return false;
    try {
      const sub = await firstValueFrom(this.swPush.subscription);
      return !!sub;
    } catch {
      return false;
    }
  }

  startNotificationClickHandling(): void {
    if (this.clickListenerStarted || !this.swPush.isEnabled) return;
    this.clickListenerStarted = true;
    this.swPush.notificationClicks.subscribe(({ notification }) => {
      const data = notification?.data as { url?: string } | undefined;
      const url = data?.url;
      if (url && typeof url === 'string' && url.startsWith('/')) {
        void this.router.navigateByUrl(url);
      }
    });
  }

  async enable(): Promise<void> {
    if (!this.isSupported) {
      throw new Error('Push notifications are not available on this device');
    }
    const sub = await this.swPush.requestSubscription({
      serverPublicKey: this.env.vapidPublicKey.trim(),
    });
    const keys = subscriptionKeys(sub);
    try {
      await firstValueFrom(
        this.pushSubscriptionService.upsertSubscription(
          keys.endpoint,
          keys.p256dh,
          keys.auth,
        ),
      );
    } catch (err) {
      try {
        await this.swPush.unsubscribe();
      } catch {
        // DB 未登録のまま端末購読だけ残さない
      }
      throw err;
    }
  }

  async disable(): Promise<void> {
    if (!this.swPush.isEnabled) return;
    const sub = await firstValueFrom(this.swPush.subscription);
    if (sub) {
      const endpoint = sub.endpoint;
      await this.swPush.unsubscribe();
      try {
        await firstValueFrom(
          this.pushSubscriptionService.deleteSubscription(endpoint),
        );
      } catch {
        // ローカル解除は成功していればよい
      }
    }
  }
}
